import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { buildContractDataFromReservation, sendAndDownloadContract } from '../utils/contract';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import DatePicker from 'react-datepicker';
import { shift } from '@floating-ui/react';
import { registerLocale } from 'react-datepicker';
import pl from 'date-fns/locale/pl';
import axios from 'axios';
import { useTranslation } from '../translations';
import { useToast } from '../components/ui/Toast';
import { formatMoney } from '../utils/format';
import { API_BASE } from '../config';
import InventoryModal from '../components/InventoryModal';
import SimpleSelect from '../ui/SimpleSelect';
import { useNavigate } from 'react-router-dom'; // NOWE: import nawigacji
import { fetchPriceQuote, quoteKey } from '../utils/priceQuote';
import { dniPlatne, dniPlatneZWyceny, pokazDniPlatne } from '../utils/dniPlatne';
import { useTabelaDniPlatnych } from '../utils/useTabelaDniPlatnych';
import { IconBadge, panelTopHairline } from '../ui/common';
import { makeThumbnail, prepareUploadPhoto } from '../utils/imageThumb';
import { wyslijPoKolei, komunikatNowejRezerwacji } from '../utils/wysylkaDokumentacji';
import { buildPriceBreakdown, groupEquipmentForDisplay } from '../utils/priceBreakdown';
import { ItemRate, RuleBadges, BundleFrame, QuotePendingNote, SummaryBreakdownRows } from '../components/booking/PriceBreakdown';
import { scrollToTop } from '../utils/scrollToTop';
import ScanSources from '../components/scanner/ScanSources';
import useBarcodeScan from '../utils/useBarcodeScan';
import { findEquipmentByCode } from '../utils/equipmentCode';
import { lookupEquipmentByCode } from '../components/scanner/scanApi';
import { AmountField, DiscountField, AmountNote } from '../ui/AmountField';
import { amountOrZero, amountToRaw, discountInPln, discountError, advanceError, amountErrorMessage, roundToCents } from '../utils/amountField';
import useAmountChecks from '../utils/useAmountChecks';
import { maPremium } from '../utils/planFirmy';
import { maKontakt, bledyKontaktu } from '../utils/kontaktKlienta';
import PaymentDeadlineField, { useTerminWplaty } from '../components/payments/PaymentDeadlineField';
import PremiumNotice from '../components/premium/PremiumNotice';
import { usePremiumBlad } from '../components/premium/PremiumUpgrade';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// Kalendarzyk dat dosuwany do krawędzi ekranu. Na dotyku dni mają 44 px, więc kalendarzyk jest
// szerszy (index.css), a react-datepicker 8 sam go tylko odwraca (flip), nie przesuwa: na telefonie
// 390 px strzałka następnego miesiąca i niedziela chowały się za krawędzią (przegląd gałęzi, 28.09.2026).
const DOSUN_DO_EKRANU = [shift({ padding: 8 })];
registerLocale('pl', pl);

// DODANE: pomocnicze liczenie dni inkluzywnie (Od i Do włącznie)
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};
const formatYMD = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const daysInclusive = (from, to) => {
  const s = startOfDay(from);
  const e = startOfDay(to);
  if (!s || !e) return 0;
  const diff = Math.round((e - s) / MS_PER_DAY);
  return diff >= 0 ? diff + 1 : 0;
};

// helper: sprawdzenie czy data jest w przeszłości (dzień bez czasu)
const isPastDate = (date) => {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const getEquipmentDailyRate = (equipment, isLongTermRental) => {
  if (!equipment) return 0;
  const base = Number(equipment.price_per_day ?? equipment.price ?? 0) || 0;
  const longTerm = Number(equipment.price_over_7 ?? 0) || 0;
  return isLongTermRental && longTerm > 0 ? longTerm : base;
};

// SVG Icons
const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const CreditCardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const TagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="7" y1="7" x2="7.01" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PackageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
    <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Dodaj na początku (po innych importach):
const DocumentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="3,6 5,6 21,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DotIcon = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="4" cy="4" r="3" fill="currentColor"/>
  </svg>
);

const Container = styled.div`
  max-width: 100%; /* pełna szerokość z marginesami */
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: visible; /* ZMIANA: było hidden – obcinało popper kalendarza */
  min-height: calc(100vh - 48px);
  
  @media (min-width: 1400px) {
    max-width: 1600px;
    padding: 24px 32px;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
  
  ${panelTopHairline}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

`;

const TitleIcon = styled.div`
  color: var(--info);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  color: var(--text-muted);
  font-weight: 400;
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Panel = styled.section`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  /* Panel jest komorka siatki, a te maja domyslnie min-width: auto, wiec NIE ZWEZAJA SIE
     ponizej wlasnej zawartosci — zamiast tego rozpychaja strone. Na telefonie sekcja terminu
     miala przez to 410px przy ekranie 390 i wychodzila poza kadr. Zero tego nie widzialo,
     bo index.css ma overflow-x: hidden, wiec tresc byla UCINANA, a nie przewijana. */
  min-width: 0;
  margin-bottom: 24px;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

// DODANE: panel z wyższym z-index dla sekcji "Dane podstawowe"
const BasicDataPanel = styled(Panel)`
  position: relative;
  z-index: 200; /* ponad kolejnymi panelami */
`;

// DODANE: panel dla sekcji Terminu
const TermPanel = styled(Panel)`
  position: relative;
  z-index: 190; /* wyżej niż późniejsze panele (np. Wybrany sprzęt) */
`;

// DODAJ pod TermPanel (lub obok BasicDataPanel / TermPanel)
const PaymentPanel = styled(Panel)`
  position: relative;
  z-index: 185; /* ponad panelem Wybrany sprzęt (który ma z-index auto) */
`;

const DepositInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
    background: var(--card-bg);
  }

  &:disabled {
    background: var(--table-row-hover);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  /* ukrycie strzałek */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    display: none;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

// DODANE: globalny theme dla react-datepicker
const DatepickerTheme = createGlobalStyle`
  .react-datepicker {
    width: 252px; /* zwężone (dopasowane do siatki dni) */
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: var(--card-bg);
    border: 1px solid var(--input-border);
    border-radius: 14px;
    box-shadow: 0 10px 34px var(--shadow-color);
    overflow: hidden;
    padding: 0 0 6px 0;
  }
  .react-datepicker__month { margin: 4px 6px 0 6px; }

  /* Dni przeszłe (dzisiejszy miesiąc) */
  .react-datepicker__day.past-day:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--keyboard-selected) {
    color: var(--text-muted);
    opacity: 0.6;
  }

  .react-datepicker__triangle { display: none; }

  .react-datepicker-popper[data-placement^="bottom"] {
    margin-top: 6px; /* mniej */
    z-index: var(--z-popover) !important;
  }

  .react-datepicker__header {
    background: var(--primary);
    border-bottom: 1px solid rgba(255,255,255,0.15);
    padding-top: 6px;
    padding-bottom: 4px;
  }

  .react-datepicker__current-month {
    color: var(--primary-contrast);
    font-weight: 600;
    font-size: 0.90rem;
    letter-spacing: .3px;
  }

  .react-datepicker__day-names {
    margin-top: 2px;
  }

  .react-datepicker__day-name {
    color: rgba(255,255,255,0.85);
    font-weight: 500;
    width: 32px;
    line-height: 32px;
    margin: 1px;
    font-size: 0.65rem;
  }

  .react-datepicker__month {
    margin: 4px 6px 0 6px;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day {
    width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 1px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.78rem;
    color: var(--text);
    transition: all .15s ease;
    outline: none;
  }

  .react-datepicker__day:hover {
    background: var(--table-row-hover);
    cursor: pointer;
  }

  .react-datepicker__day--outside-month {
    opacity: 0.30;
  }

  .react-datepicker__day--today:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    font-weight: 600;
    box-shadow: inset 0 0 0 2px rgba(59,130,246,0.45);
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: var(--primary);
    color: var(--primary-contrast) !important;
    box-shadow: var(--shadow-primary);
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background: rgba(59,130,246,0.18);
    color: var(--text);
    border-radius: 8px;
  }

  /* Weekend highlight (nie nadpisuj wybranych) */
  .react-datepicker__day.weekend:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--keyboard-selected) {
    color: var(--danger);
  }
  .react-datepicker__day.weekend:hover:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    background: rgba(239,68,68,0.12);
  }

  .react-datepicker__navigation {
    top: 8px;
  }
  .react-datepicker__navigation-icon::before {
    border-color: var(--primary-contrast);
  }

  [data-theme='dark'] .react-datepicker {
    background: var(--panel-bg);
  }
`;

const RangeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--input-bg, rgba(255,255,255,0.06));
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--input-border)'};
  border-radius: 14px;
  padding: 8px 8px;
  min-height: 56px;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s, background .2s;
  font-size: 0.95rem;
  max-width: 360px;
  width: 100%;

  /* Na telefonie dwa pola dat obok siebie dostaja po ~150px kazde, co jest ciasne dla daty,
     a do tego kontener nie schodzil ponizej 360px i wychodzil poza ekran (siatka i elementy
     elastyczne maja domyslnie min-width: auto, a szerokosc procentowa liczyla sie z zapetleniem
     przez inline-blockowe opakowania react-datepickera). Ukladamy je wiec jedno pod drugim:
     to zdejmuje twarde 360px i daje kazdej dacie pelna szerokosc. */
  @media (max-width: 430px) {
    flex-direction: column;
    align-items: stretch;
    max-width: none;
    gap: 6px;

    .range-sep {
      text-align: center;
      transform: rotate(90deg);
    }
  }

  &:focus-within, &.focus-visible-proxy {
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: ${props => props.error
      ? '0 0 0 4px rgba(239, 68, 68, 0.15)'
      : '0 0 0 4px rgba(59,130,246,.12)'};
  }

  input {
    flex: 1;
    /* Bez tego pole tekstowe nie schodzi ponizej swojej naturalnej szerokosci (~166px dla
       domyslnych 20 znakow), wiec dwa pola + strzalka + odstepy daja twarde 360px, ktorych
       width: 100% nie jest w stanie scisnac. To ono rozpychalo cala sekcje. */
    min-width: 0;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--text);
    line-height: 1.2;
    padding: 0;
    cursor: pointer;
  }

  input::placeholder {
    color: rgba(100,116,139,0.38);
    font-weight: 400;
  }

  &.filled {
    background: rgba(59,130,246,0.06);
  }
  
  .range-sep {
    color: var(--text-muted);
  }
`;

const DatepickerZIndexFix = styled.div`
  .react-datepicker-popper {
    z-index: var(--z-popover) !important;
  }

  /* react-datepicker opakowuje pole w dwa DIV-y o display: inline-block, ktore dopasowuja sie
     do TRESCI. Przez to width: 100% na naszym kontenerze nie mial sie do czego odniesc i pole
     zakresu dat trzymalo stale 360px takze na ekranie 390px, wychodzac poza kadr. */
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: block;
    width: 100%;
    min-width: 0;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  /* Tytul panelu zjadal szerokosc, a przycisk obok kurczyl sie ponizej swojej etykiety:
     „Wybierz sprzet" dostawal 74px zamiast 93 i napis byl uciety. Zawijanie przenosi go
     do nastepnego wiersza zamiast go scisnac. */
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--input-border);

  /* Na telefonie tytul panelu i przycisk obok siebie nie mieszcza sie: przycisk dostawal 74px
     przy potrzebnych 145 i etykieta byla przycieta („Wybie sprze").

     NIE zmieniamy tu kierunku na kolumne, choc tak bylo najpierw: PanelHeader uzywaja WSZYSTKIE
     panele tego ekranu (osiem), a przycisk ma tylko jeden, pozostale siedem dostawalo wtedy ikone
     nad tytulem zamiast obok. Znalezione w przegladzie kodu. Zamiast tego przycisk (jesli w ogole
     jest) bierze wlasny wiersz, a ikona i tytul zostaja w jednej linii jak dotad. */
  @media (max-width: 560px) {
    button {
      margin-left: 0 !important;
      flex: 1 1 100%;
      justify-content: center;
    }
  }
`;

// Wspólny wzorzec plakietki ikony z ui/common.js (IconBadge) zamiast lokalnej kopii pełnej
// niebieskiej wypełnionej ikony — wołający przekazuje `$tone="steel"` itp. zamiast
// `bg="var(--primary)"`, patrz komentarz przy ICON_TONES w ui/common.js.
const PanelIcon = IconBadge;

const PanelTitle = styled.h3`
  margin: 0;
  /* Baza zerowa, a nie auto. Przy flex-wrap o przeniesieniu do nowego wiersza decyduje
     NATURALNA szerokosc elementu, nie min-width, wiec dlugi tytul („Dokumentacja stanu
     sprzetu") schodzil pod ikone. Z baza 0 tytul nigdy nie lamie wiersza, tylko zajmuje
     reszte miejsca i zawija sie w srodku. */
  flex: 1 1 0;
  min-width: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  
  @media(max-width: 1024px) {
    grid-template-columns: 1fr; /* jeden panel pod drugim na tabletach */
  }
  
  @media(max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  
  @media(min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  /* Element elastyczny ma domyslnie min-width: auto, wiec nie schodzi ponizej wlasnej tresci
     i zamiast sie zwezic, wypycha wszystko ponizej poza ekran. To bylo ostatnie ogniwo
     rozpychajace sekcje terminu na telefonie. */
  min-width: 0;
  position: relative;
  padding-bottom: 18px; /* Dodatkowe miejsce na komunikat błędu */
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--input-border)'};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500; /* keep normal weight */
  background: var(--input-bg);
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--text);
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: ${props => props.error
      ? '0 0 0 4px rgba(239, 68, 68, 0.15)'
      : '0 0 0 4px rgba(59, 130, 246, 0.15)'};
  }
  
  &::placeholder {
    color: rgba(100,116,139,0.38);
    font-weight: 400;
    transition: color .12s ease;
  }

  /* kiedy pole ma wartość (nie placeholder) - nie podbijamy tak mocno wagi fontu */
  &:not(:placeholder-shown) {
    color: var(--text);
    font-weight: 500;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }


  select {
    border: none;
    background: transparent;
    height: 100%;
    padding: 0 12px;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text);
    outline: none;
    appearance: none;
    border-left: 1px solid var(--input-border);
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    cursor: pointer;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const FieldError = styled.div`
  position: absolute;
  bottom: 0;
  left: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--danger);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 24px;
  
  @media(max-width: 768px) {
    justify-content: stretch;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media(max-width: 768px) {
    flex: 1;
  }
`;

const SecondaryButton = styled.button`
  /* Etykieta ma zostac cala. Same flex-shrink: 0 nie wystarcza: przy white-space: normal
     przegladarka uznaje, ze napis moze sie zlamac, wiec przycisk „miesci sie" w 74px i tekst
     i tak jest przycinany. Dopiero nowrap czyni jego minimalna szerokosc rowna etykiecie,
     przez co wiersz elastyczny przenosi go nizej zamiast sciskac. */
  flex-shrink: 0;
  white-space: nowrap;
  background: ${props => props.active ? 'var(--primary)' : 'var(--input-bg)'};
  color: ${props => props.active ? 'white' : 'var(--text)'};
  border: ${props => props.active ? 'none' : '2px solid var(--input-border)'};
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${props => props.active ? '0 8px 30px rgba(59,130,246,0.18)' : 'none'};

  &:hover:not(:disabled) {
    ${props => props.active ? 'transform: translateY(-2px); box-shadow: var(--shadow-lg);' : 'background: var(--table-row-hover); border-color: var(--primary); color: var(--primary); transform: translateY(-1px);'}
  }

  &:disabled {
    opacity: 0.6;
    transform: none;
  }

  @media(max-width: 768px) {
    flex: 1;
  }

  /* Na telefonie przycisk stoi w wierszu z polem albo z innymi przyciskami i dostaje resztke
     szerokosci, przez co etykieta jest przycinana („+ Dodaj do dokumentacji" miescilo sie w
     66px przy potrzebnych 204). Wlasny wiersz na pelna szerokosc to zarazem typowy uklad
     przyciskow akcji na telefonie. */
  @media (max-width: 560px) {
    flex: 1 1 100%;
    justify-content: center;
  }
`;

const EquipmentButton = styled(PrimaryButton)`
  /* Etykieta ma zostac cala. Same flex-shrink: 0 nie wystarcza: przy white-space: normal
     przegladarka uznaje, ze napis moze sie zlamac, wiec przycisk „miesci sie" w 74px i tekst
     i tak jest przycinany. Dopiero nowrap czyni jego minimalna szerokosc rowna etykiecie,
     przez co wiersz elastyczny przenosi go nizej zamiast sciskac. */
  flex-shrink: 0;
  white-space: nowrap;
  background: ${props => props.enabled ?
    'var(--success)' :
    'var(--text-muted)'};
  cursor: ${props => props.enabled ? 'pointer' : 'not-allowed'};
`;

// P5.2 (restyle): sekcja dokumentacji spójna z resztą formularza. Wybór sprzętu przez
// własny SimpleSelect (natywny <select> na Safari/macOS gubił widoczność opcji).
const DocFileButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: 2px dashed var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover { border-color: var(--primary); color: var(--primary); }
  input { display: none; }
`;

const DocEntry = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  background: var(--card-bg);
`;

const DocRemove = styled.button`
  background: none;
  border: none;
  color: var(--danger);
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 4px 6px;
  &:hover { text-decoration: underline; }
`;

const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const Message = styled.div`
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  background: ${props => props.error ? 'var(--danger-bg)' : 'var(--success-bg)'};
  color: ${props => props.error ? 'var(--danger)' : 'var(--success)'};
  border: 1px solid ${props => props.error ? 'var(--danger-border)' : 'var(--success-border)'};
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`;

// Akcje pod komunikatem o ofercie, z której powstała już rezerwacja (409 offer_already_converted).
const OfferGoneActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: -8px 0 20px;
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: calc(100% - 15px);
  left: 0; right: 0;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  z-index: var(--z-dropdown);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 0;
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--input-border);
  transition: background 0.2s ease;
  color: var(--text);
  
  &:hover { background: var(--table-row-hover); }
  &:last-child { border-bottom: none; }
`;

const DiscountInputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  transition: all 0.2s ease;
  margin-top: 13px;
  padding: 8px;
  
  &:focus-within {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
`;

const DiscountInput = styled.input`
  flex: 1;
  padding: 12px;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-weight: 500;
  text-align: right;

  &:focus {
    outline: none;
  }

  /* ukryj strzałki (spinner) dla input[type=number] */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const EquipmentList = styled.div`
  background: var(--panel-bg);
  border-radius: 12px;
  padding: 16px;
  margin-top: 8px;
  border: 1px solid var(--input-border);
`;

const EquipmentItem = styled.div`
  padding: 8px 12px;
  margin-bottom: 6px;
  background: var(--card-bg);
  border-radius: 8px;
  border: 1px solid var(--input-border);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;

  &:last-child { margin-bottom: 0; }

  /* Telefon (test na iPhonie 17, 18.10.2026): opis, cena i przycisk w jednym rzędzie ściskały
     nazwę do 3 linii, a szczegóły do 5, a przycisk kosza (na dotyku 44px, index.css) wystawał za
     ramkę wiersza. Na wąskim ekranie opis zajmuje cały pierwszy rząd, cena i kosz drugi. */
  @media (max-width: 560px) {
    flex-wrap: wrap;
  }
`;

const EquipmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  @media (max-width: 560px) {
    flex: 1 1 100%;
    min-width: 0;
    flex-wrap: wrap;

    /* Szczegóły (numer, rozmiar, marka…) w osobnej linii pod nazwą, łamane w dowolnym miejscu. */
    > span {
      flex-basis: 100%;
      overflow-wrap: anywhere;
      color: var(--text-muted);
    }
  }
`;

// Cena za dzień z odznakami cennika: na komputerze przy prawej krawędzi, na telefonie w drugim
// rzędzie od lewej, obok kosza.
const EquipmentPriceBox = styled.div`
  text-align: right;

  @media (max-width: 560px) {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
  }
`;

const EquipmentPrice = styled.span`
  font-weight: 600;
  color: var(--primary);
  margin-right: 12px;
`;

const RemoveButton = styled.button`
  flex-shrink: 0;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--danger);
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.4);
    transform: scale(1.1);
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: visible; /* pozwala, żeby absolutny listbox nie był obcinany */
  z-index: 0;
`;

const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  min-height: 48px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--input-border)'};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;
  box-sizing: border-box;

  /* prevent label wrapping so button height stays consistent */
  & > span:first-child {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 28px);
  }
  
  &:hover {
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
  }
  &:focus-visible {
    outline: none;
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: ${props => props.error
      ? '0 0 0 4px rgba(239, 68, 68, 0.15)'
      : '0 0 0 4px rgba(59,130,246,.15)'};
  }
  &[data-open="true"] {
    border-color: ${props => props.error ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: ${props => props.error
      ? '0 0 0 4px rgba(239, 68, 68, 0.15)'
      : '0 0 0 4px rgba(59,130,246,.15)'};
    
    svg {
      color: var(--primary);
    }
  }
  &[data-placeholder="true"] {
    span:first-child {
      color: var(--text-muted);
      font-weight: 400;
    }
  }
  
  svg {
    flex-shrink: 0;
    transition: color .2s;
    color: var(--text-muted);
  }
`;

const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100%);
  left: 0;
  width: 100%;
  max-height: 200px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-dropdown);
`;

const FilterOptionItem = styled.li`
  padding: 10px 12px;

  /* Opcje list w nowej rezerwacji (m.in. „%” i „zł”) mialy 38px (przeglad galezi, 28.09.2026). */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    min-height: 44px;
    display: flex;
    align-items: center;
  }
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
      color: var(--primary);
      flex-shrink: 0;
    }
    
    .option-label {
      flex: 1;
    }
  }

  &:hover {
    background: var(--table-row-hover);
  }
  
  &[data-selected="true"] {
    background: rgba(59,130,246,0.15);
    color: var(--primary);
    font-weight: 600;
  }
`;

// Dodano: przycisk specjalny dla pola Sposób płatności — wyrównany wysokością do pola rabatu
const PaymentDropdownButton = styled(FilterDropdownButton)`
  min-height: 44px; /* dopasowane do height pola rabat w calendar.js */
  padding: 8px 12px;
  align-items: center;
  box-sizing: border-box;
`;

// U.25: pole rabatu i zaliczki oraz komunikat pod polem (`AmountNote`) to komponenty
// z ui/AmountField.js; tu tylko szerokość pola na tej stronie.
const AmountBox = styled.div`
  max-width: 260px;
`;


const SummaryTable = styled.div`
  background: var(--panel-bg);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--input-border);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--input-border);
  
  &:last-child {
    border-bottom: none;
    background: rgba(59,130,246,0.10);
    font-weight: 700;
    color: var(--text);
  }
`;

const SummaryLabel = styled.span`
  font-weight: 600;
  color: var(--text);
`;

const SummaryValue = styled.span`
  font-weight: 500;
  color: var(--text);
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 32px;
  max-width: 900px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px var(--shadow-color);
  border: 1px solid var(--input-border);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--input-border);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 12px; /* zwiększone z domyślnego */
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: var(--danger);
  }
`;

const FilterRow = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(6, 1fr);
  margin-bottom: 20px;
`;

const FilterInput = styled(Input)`
   padding: 12px 16px;
   font-size: 1rem;
   min-height: 48px;
   box-sizing: border-box;
`;

const FilterSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  margin-bottom: 6px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

const EquipmentGrid = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--input-border);
  border-radius: 0 0 12px 12px; /* dopasowanie do nagłówka */
  background: var(--panel-bg);
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  border-top: 1px solid var(--input-border);
  background: var(--panel-bg);
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 2px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  min-width: 60px;
  text-align: center;
`;

const EquipmentHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(59,130,246,0.08);
  border: 1px solid var(--input-border);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  font-weight: 700;
  color: var(--text);
  gap: 8px;

  .col-checkbox { width: 40px; flex-shrink: 0; }
  .col-category { flex: 1.3; min-width: 110px; }
  .col-brand { flex: 1; min-width: 90px; }
  .col-name { flex: 2.5; }
  .col-size { flex: 0.8; min-width: 70px; }
  .col-number { flex: 1; min-width: 90px;  }
  .col-class { flex: 0.8; min-width: 70px; }
`;

const EquipmentRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--input-border);
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? 'rgba(59, 130, 246, 0.12)' : 'transparent'};
  font-size: 0.85rem;
  gap: 8px;

  &:hover {
    background: ${props => props.selected ? 'rgba(59,130,246,0.18)' : 'var(--table-row-hover)'};
  }
  &:last-child {
    border-bottom: none;
  }

  .col-checkbox { width: 40px; flex-shrink: 0; display: flex; align-items: center; }
  .col-category { flex: 1.3; min-width: 110px; }
  .col-brand { flex: 1; min-width: 90px; }
  .col-name { flex: 2.5; display: flex; align-items: center; gap: 8px; font-weight: 600; }
  .col-size { flex: 0.8; min-width: 70px; }
  .col-number { flex: 1; min-width: 90px; font-weight: 600; color: var(--text-muted); }
  .col-class { flex: 0.8; min-width: 70px; }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--primary);
  cursor: pointer;
`;

const RangeInputsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RangeSeparator = styled.div`
  text-align: center;
  color: var(--text);
  font-size: 1rem;
  padding: 4px 0;
`;

const RangeInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 2px solid var(--input-border);
  border-radius: 10px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.95rem;
  box-sizing: border-box;
  -moz-appearance: textfield;
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

function FilterDropdown({ value, onChange, options, placeholder, children, showSearch = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [query, setQuery] = useState('');

  const close = () => { setOpen(false); };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', handler, true);
    const onWheel = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT' && t.type === 'number' && ref.current && ref.current.contains(t)) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', handler, true);
      document.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [open]);

  useEffect(() => {
    if (open) setQuery('');
  }, [open, value]);

  const selected = options.find(o => o.value === value);
  const q = query.trim().toLowerCase();
  // E.5: etykieta bywa NULL-em — sprzęt bez marki/rozmiaru (Equipment.brand/size są
  // NULL-able od migracji 040). `o.label.toString()` wywalało cały formularz rezerwacji
  // („Cannot read properties of null”), więc lista sprzętu w ogóle się nie renderowała.
  const filteredOptions = options.filter(o => String(o.label ?? '').toLowerCase().includes(q));

  return (
    <DropdownWrapper ref={ref}>
      <FilterDropdownButton
        type="button"
        data-open={open}
        data-placeholder={!selected}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span>{selected ? selected.label : placeholder}</span>
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </FilterDropdownButton>

      {open && (
        <FilterOptionsList role="listbox">
          {showSearch && (
            <li style={{ padding: '2px 4px' }}>
              <FilterSearchInput
                placeholder="Filtruj..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </li>
          )}
          {children && (
            <li style={{ padding: '8px 12px' }}>
              {children}
            </li>
          )}

          {filteredOptions.map(o => {
            const sel = o.value === value;
            return (
              <FilterOptionItem
                key={o.value}
                data-selected={sel}
                onClick={() => { onChange(o.value); close(); }}
              >
                <span className="option-content">
                  {sel && <DotIcon />}
                  <span className="option-label">{o.label}</span>
                </span>
              </FilterOptionItem>
            );
          })}

          {!filteredOptions.length && (
            <FilterOptionItem data-selected={false} style={{ cursor: 'default', opacity: 0.7 }}>
              <span className="option-content">
                <span className="option-label">{t('common.noResults')}</span>
              </span>
            </FilterOptionItem>
          )}
        </FilterOptionsList>
      )}
    </DropdownWrapper>
  );
}

// Usuń istniejący komponent PaymentMethodDropdown i zastąp go tym:
function PaymentMethodDropdown({ label, required, value, onChange, options, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [ activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const close = () => { setOpen(false); setActiveIndex(-1); };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('scroll', handler, true);
    };
  }, [open]);

  const selectedObj = options.find(o => o.value === value);

  return (
    <div style={{ marginBottom: 0 }}>
      <Label style={{ marginBottom: 6 }}>
        <CreditCardIcon />
        {label}{required && '*'}
      </Label>
      <DropdownWrapper ref={wrapperRef}>
        <PaymentDropdownButton
          type="button"
          data-open={open}
          data-placeholder={!selectedObj}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          error={!!error}
        >
          <span>{selectedObj ? selectedObj.label : (placeholder || 'Wybierz płatność')}</span>
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </PaymentDropdownButton>
        {open && (
          <FilterOptionsList role="listbox">
            {options.map(o => {
              const sel = o.value === value;
              return (
                <FilterOptionItem
                  key={o.value}
                  data-selected={sel}
                  onClick={() => { onChange(o.value); close(); }}
                >
                  <span className="option-content">
                    {sel && <DotIcon />}
                    <span className="option-label">{o.label}</span>
                  </span>
                </FilterOptionItem>
              );
            })}
          </FilterOptionsList>
        )}
      </DropdownWrapper>
      {error && <FieldError><XIcon /> {error}</FieldError>}
    </div>
  );
}

const formatLocalDate = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function Reservation() {
  const { t, currentLanguage } = useTranslation();
  const toast = useToast();
  const pokazBladPremium = usePremiumBlad();
  const navigate = useNavigate(); // NOWE: hook nawigacji

  const initialForm = {
    firstname: '',
    lastname: '',
    email: '',
    city: '',
    postal_code: '',
    street: '',
    building_number: '',
    apartment_number: '',
    idNumber: '',
    phone: '',
    // U.25: surowy tekst pola, liczbę daje utils/amountField.js (puste pole to nie „0").
    discount: '',
    discountUnit: '%',
    note: '',
    // USUNIĘTE: paymentMethod
    advanceAmount: '',       // surowy tekst pola zaliczki (U.25)
    advancePaid: false,       // NOWE: czy opłacona
    sendPayLink: false,       // P3.3-B: po zapisaniu wyślij klientowi link do zapłaty zaliczki
    depositOverrideRaw: '',
  };

  const [form, setForm] = useState(initialForm);
  const [dateFrom, setDateFrom] = useState(null);
  // F.6: termin wpłaty zaliczki przy linku wysyłanym po zapisie (Premium).
  const terminWplaty = useTerminWplaty(dateFrom);
  const [dateTo, setDateTo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  // Które pola kwot pokazują limit; sam komunikat jest wyliczany przy renderze z bieżących wartości
  // (niżej, przy `toPay`). W `validationErrors.discount`/`.advanceAmount` zostaje tylko komunikat
  // po błędzie zgłoszonym przez serwer.
  const {
    shown: amountChecksShown, show: showAmountCheck, hide: hideAmountCheck, showAll: showAmountChecks,
  } = useAmountChecks();
  const [availabilityWarning, setAvailabilityWarning] = useState('');
  const [pendingRange, setPendingRange] = useState(null);
  const [sourceOfferId, setSourceOfferId] = useState(null);
  // N.6: kwota i rabat rezerwacji z oferty z wypełnienia (GET /api/sent-offers, Login/backend/lib/kwotaOferty.js),
  // termin i sprzęt oferty. Dopóki formularz ma ten sam sprzęt i termin, serwer zapisze rezerwację
  // z kwotą i rabatem oferty, więc formularz pokazuje te liczby zamiast bieżącej wyceny.
  const [cenaOferty, setCenaOferty] = useState(null);
  // Serwer odpowiedział, że z oferty z wypełnienia powstała już rezerwacja (409). Ponowny zapis
  // z tą samą ofertą dałby ten sam błąd, więc formularz go nie wysyła, dopóki użytkownik nie
  // odłączy oferty albo nie przejdzie do kalendarza (przegląd końcowy 05.10.2026).
  const [offerConverted, setOfferConverted] = useState(false);
  const [showPastConfirm, setShowPastConfirm] = useState(false);
  const [priceQuote, setPriceQuote] = useState(null); // P7.2: { key, data } from /api/price-quote
  const [quotePending, setQuotePending] = useState(false); // P7.5: debounced fetch in flight
  // Klucz zaznaczenia (sprzęt + daty), dla którego wycena już WRÓCIŁA, udana albo nie (27.10.2026,
  // jak w edycji rezerwacji). Do tego czasu kwota to cena lokalna bez cen dynamicznych i zestawów,
  // a zapis tuż po wybraniu sprzętu zapisywał ją jako kwotę rezerwacji. Flaga `quotePending` się do
  // tego nie nadaje: ustawia ją efekt, więc w renderze ze zmianą sprzętu jest jeszcze nieaktualna.
  const [quoteSettledKey, setQuoteSettledKey] = useState(null);
  const tabelaDniPlatnych = useTabelaDniPlatnych();
  // P5.2: dokumentacja stanu sprzętu przy WYDANIU — buforowana w formularzu (rezerwacja nie
  // ma jeszcze ID), wgrywana do /api/reservation-photos/:id dopiero po utworzeniu rezerwacji.
  const [pendingDocs, setPendingDocs] = useState([]);
  const [docNote, setDocNote] = useState('');
  const [docEquipmentId, setDocEquipmentId] = useState('');
  const [docFiles, setDocFiles] = useState([]); // P5.6: wiele zdjęć naraz
  const docFileRef = useRef(null);
  const addPendingDoc = () => {
    if (!docFiles.length && !docNote.trim()) { toast.error(t('reservationForm.photos.addRequired')); return; }
    // Każdy plik = osobny wpis; notatka dopina się do pierwszego (albo sama, gdy brak zdjęć).
    const entries = docFiles.length
      ? docFiles.map((f, i) => ({
          id: `${Date.now()}_${i}_${Math.random()}`,
          note: i === 0 ? docNote.trim() : '',
          equipmentId: docEquipmentId,
          file: f,
          fileName: f.name,
          previewUrl: URL.createObjectURL(f), // utworzony RAZ, zwalniany przy usunięciu
        }))
      : [{ id: `${Date.now()}_${Math.random()}`, note: docNote.trim(), equipmentId: docEquipmentId, file: null, fileName: '', previewUrl: null }];
    setPendingDocs(prev => [...prev, ...entries]);
    setDocNote(''); setDocEquipmentId(''); setDocFiles([]);
    if (docFileRef.current) docFileRef.current.value = '';
  };
  const removePendingDoc = (id) => setPendingDocs(prev => {
    const d = prev.find(x => x.id === id);
    if (d && d.previewUrl) URL.revokeObjectURL(d.previewUrl);
    return prev.filter(x => x.id !== id);
  });
  // Sprzątanie podglądów blob: przy odmontowaniu (wyjście z formularza bez zapisu).
  // Ref trzyma najświeższą listę; podwójny revoke (po zapisie + tu) jest bezpiecznym no-opem.
  const pendingDocsRef = useRef(pendingDocs);
  useEffect(() => { pendingDocsRef.current = pendingDocs; }, [pendingDocs]);
  useEffect(() => () => {
    pendingDocsRef.current.forEach((d) => { if (d.previewUrl) URL.revokeObjectURL(d.previewUrl); });
  }, []);
  const selectedEquipmentRef = useRef(selectedEquipment);

  useEffect(() => {
    selectedEquipmentRef.current = selectedEquipment;
  }, [selectedEquipment]);

  // Skan naklejki dodaje sztukę do rezerwacji (czytnik USB, aparat, telefon — ten sam kod).
  // `equipmentList` to sprzęt WOLNY w wybranym terminie, więc brak trafienia nie znaczy jeszcze
  // „nie ma takiej sztuki": dopytujemy serwer, żeby powiedzieć „zajęta w tym terminie" zamiast
  // mylącego „nieznany kod" przy naklejce, którą pracownik trzyma w ręku.
  // Sprzęt wolny w wybranym terminie. Wspólne dla okna wyboru i skanu.
  const fetchAvailableEquipment = useCallback(async () => {
    const params = new URLSearchParams({
      start: formatLocalDate(dateFrom),
      end: formatLocalDate(dateTo)
    });
    if (sourceOfferId) {
      params.set('excludeOfferId', String(sourceOfferId));
    }
    const res = await axios.get(`${API_BASE}/api/equipment/available?${params}`, { withCredentials: true });
    return res.data;
  }, [dateFrom, dateTo, sourceOfferId]);

  const handleScanAdd = useCallback(async (code) => {
    let wynik;
    if (!dateFrom || !dateTo) {
      wynik = { tone: 'warn', text: t('reservation.scan.pickDatesFirst') };
    } else {
      // Lista ze stanu jest wypełniana dopiero przy otwarciu okna wyboru (i mogła zostać
      // z innego terminu), więc skan pyta serwer o wolny sprzęt za każdym razem.
      let wolne = equipmentList;
      try {
        wolne = await fetchAvailableEquipment();
        setEquipmentList(wolne);
      } catch (_) { /* zostaje lista ze stanu */ }
      const hit = findEquipmentByCode(code, wolne);
      if (hit && selectedEquipmentRef.current.includes(hit.id)) {
        wynik = { tone: 'warn', text: t('reservation.scan.alreadyAdded').replace('{name}', hit.name) };
      } else if (hit) {
        setSelectedEquipment((prev) => (prev.includes(hit.id) ? prev : [...prev, hit.id]));
        wynik = { tone: 'ok', text: t('reservation.scan.added').replace('{name}', hit.name) };
      } else {
        let znana = null;
        try { znana = await lookupEquipmentByCode(code); } catch (_) { znana = null; }
        wynik = znana
          ? { tone: 'error', text: t('reservation.scan.unavailable').replace('{name}', znana.name) }
          : { tone: 'error', text: t('reservation.scan.unknown').replace('{code}', code) };
      }
    }
    if (wynik.tone === 'ok') toast.success(wynik.text);
    else if (wynik.tone === 'warn') toast.warning(wynik.text);
    else toast.error(wynik.text);
    return wynik;
  }, [dateFrom, dateTo, equipmentList, fetchAvailableEquipment, t, toast]);

  // Okno wyboru sprzętu ma własną wyszukiwarkę — skan w jego trakcie trafiałby w dwa miejsca.
  useBarcodeScan(handleScanAdd, { enabled: !showEquipmentModal });

  // P7.2: debounced server quote (org price rules); falls back to the local calc
  // below on error or while in flight. Server applies the same paid days as this page,
  // so results match exactly with no rules configured.
  useEffect(() => {
    const from = formatYMD(dateFrom);
    const to = formatYMD(dateTo);
    if (!selectedEquipment.length || !from || !to) { setPriceQuote(null); setQuotePending(false); return undefined; }
    const key = quoteKey(selectedEquipment, from, to);
    let cancelled = false;
    setQuotePending(true);
    const timer = setTimeout(async () => {
      const data = await fetchPriceQuote(selectedEquipment, from, to);
      if (!cancelled) {
        setPriceQuote(data ? { key, data } : null);
        setQuotePending(false);
        setQuoteSettledKey(key);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedEquipment, dateFrom, dateTo]);

  useEffect(() => {
    if (!dateFrom || !dateTo || selectedEquipmentRef.current.length === 0) {
      setAvailabilityWarning('');
      return;
    }
    let cancelled = false;

    const verifyAvailability = async () => {
      try {
        const params = new URLSearchParams({
          start: formatLocalDate(dateFrom),
          end: formatLocalDate(dateTo)
        });
        if (sourceOfferId) {
          params.set('excludeOfferId', String(sourceOfferId));
        }
        const res = await axios.get(`${API_BASE}/api/equipment/available?${params}`, { withCredentials: true });
        if (cancelled) return;

        setEquipmentList(res.data);
        const availableIds = new Set(res.data.map(eq => eq.id));
        const currentSelection = selectedEquipmentRef.current;
        const removed = currentSelection.filter(id => !availableIds.has(id));

        if (removed.length) {
          setSelectedEquipment(prev => prev.filter(id => availableIds.has(id)));
          setAvailabilityWarning('Część wybranego sprzętu została usunięta, ponieważ jest zajęta w nowym terminie.');
          scrollToTop();
        } else {
          setAvailabilityWarning('');
        }
      } catch (err) {
        if (!cancelled) {
          setAvailabilityWarning('Nie udało się zweryfikować dostępności sprzętu. Spróbuj ponownie.');
        }
      }
    };

    verifyAvailability();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, sourceOfferId]);

  const formatLocalDate = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
  // FUNKCJA RESETUJĄCA (NOWA)
  const resetReservation = () => {
    setForm(initialForm);
    setDateFrom(null);
    setDateTo(null);
    setSelectedEquipment([]);
    setEquipmentList([]);
    setClientSuggestions([]);
    setShowSuggestions(false);
    setValidationErrors({});
    setError('');
    setSuccess('');
    // Wyczyszczenie porzuca wypełnienie z oferty, więc i powiązanie z nią: inaczej nowa,
    // wpisana od zera rezerwacja szła z numerem starej oferty (albo była blokowana po 409).
    setSourceOfferId(null);
    setCenaOferty(null);
    setOfferConverted(false);
  };

  // WPROWADZENIE DANYCH KLIENTA Z LOCALSTORAGE
  useEffect(() => {
    // Sprawdź, czy są dane klienta do wypełnienia
    const prefillData = localStorage.getItem('prefillClientData');
    if (prefillData) {
      try {
        const clientData = JSON.parse(prefillData);
        
        // Wypełnij pola formularza używając prawidłowych nazw state'ów
        setForm(prevForm => ({
          ...prevForm,
          firstname: clientData.firstname || '',
          lastname: clientData.lastname || '',
          email: clientData.email || '',
          phone: clientData.phone_number || '',
          idNumber: clientData.id_number || '',
          city: clientData.city || '',
          postal_code: clientData.postal_code || '',
          street: clientData.street || '',
          building_number: clientData.building_number || '',
          apartment_number: clientData.apartment_number || ''
        }));
        
        // Usuń dane z localStorage po użyciu
        localStorage.removeItem('prefillClientData');
        
        // Pokaż komunikat o sukcesie (jeśli masz funkcję setSuccess)
        if (typeof setSuccess === 'function') {
          setSuccess('Dane klienta zostały automatycznie wypełnione');
        }
        
        console.log('Dane klienta zostały automatycznie wypełnione:', clientData);
        
      } catch (error) {
        console.error('Błąd podczas wczytywania danych klienta:', error);
        localStorage.removeItem('prefillClientData');
      }
    }
  }, []); // Uruchom tylko raz przy ładowaniu komponentu

  // WPROWADZENIE DANYCH REZERWACJI Z OFERTY (LOCALSTORAGE)
  // Uwaga: przy uzupełnianiu pól pomijamy depozyt zwrotny (depositOverrideRaw).
  useEffect(() => {
    const prefillOfferRaw = localStorage.getItem('prefillReservationFromOffer');
    if (!prefillOfferRaw) return;

    const parseDateLocal = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value;
      const str = String(value);
      // Jeśli dostajemy "YYYY-MM-DD", wymuś lokalną północ
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(`${str}T00:00:00`);
      const d = new Date(str);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    try {
      const prefill = JSON.parse(prefillOfferRaw);

      if (prefill.dateFrom || prefill.date_from) {
        setDateFrom(parseDateLocal(prefill.dateFrom || prefill.date_from));
      }
      if (prefill.dateTo || prefill.date_to) {
        setDateTo(parseDateLocal(prefill.dateTo || prefill.date_to));
      }

      if (Array.isArray(prefill.selectedEquipment)) {
        setSelectedEquipment(prefill.selectedEquipment.filter(Boolean));
      }

      if (prefill.offerId || prefill.offer_id) {
        setSourceOfferId(prefill.offerId ?? prefill.offer_id);
        const kwota = Number(prefill.offerAmount);
        if (prefill.offerAmount != null && Number.isFinite(kwota) && kwota > 0) {
          setCenaOferty({
            kwota,
            rabatZl: Math.max(0, Number(prefill.offerDiscount) || 0),
            dni: Number(prefill.offerDays) || null,
            od: prefill.dateFrom || prefill.date_from || null,
            dd: prefill.dateTo || prefill.date_to || null,
            sprzet: Array.isArray(prefill.selectedEquipment) ? prefill.selectedEquipment.map(Number) : [],
          });
        }
      }

      setForm(prev => ({
        ...prev,
        firstname: prefill.firstname ?? prefill.firstName ?? prev.firstname ?? '',
        lastname: prefill.lastname ?? prefill.lastName ?? prev.lastname ?? '',
        email: prefill.email ?? prev.email ?? '',
        phone: prefill.phone ?? prev.phone ?? '',
        discount: amountToRaw(prefill.discount ?? prev.discount),
        discountUnit: prefill.discountUnit || prev.discountUnit || '%',
        note: prefill.note ?? prev.note ?? '',
        advanceAmount: amountToRaw(prefill.advanceAmount ?? prev.advanceAmount),
        advancePaid: Boolean(prefill.advancePaid ?? prev.advancePaid ?? false)
        // depositOverrideRaw: pomijamy celowo
      }));

      localStorage.removeItem('prefillReservationFromOffer');
      if (typeof setSuccess === 'function') {
        setSuccess('Formularz rezerwacji został uzupełniony na podstawie oferty');
      }
    } catch (e) {
      console.error('Błąd podczas wczytywania danych rezerwacji z oferty:', e);
      localStorage.removeItem('prefillReservationFromOffer');
    }
  }, []);

  // Auto-hide the special prefill success message after 5 seconds
  useEffect(() => {
    if (!success) return;
    const autoHideText = 'Formularz rezerwacji został uzupełniony na podstawie oferty';
    if (String(success) === autoHideText) {
      const id = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(id);
    }
  }, [success]);

  const openEquipmentModal = async () => {
    if (!dateFrom || !dateTo) {
      setError(t('reservation.error.selectDates'));
      scrollToTop();
      return;
    }
    setEquipmentList(await fetchAvailableEquipment());
    setShowEquipmentModal(true);
  };

  // Dodaj tę funkcję - usuwanie sprzętu z listy wybranych
  const removeEquipment = (equipmentId) => {
    setSelectedEquipment(prev => prev.filter(id => id !== equipmentId));
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  // usuń logikę podpowiedzi po numerze dowodu – zostaw proste setForm
  const handleIdNumberChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, idNumber: value });
  };

  const fetchClientSuggestions = async (value) => {
    if (!value || value.length < 2) {
      setClientSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      // Bez `withCredentials` przegladarka nie wysyla ciasteczka sesji przy zadaniu
      // cross-origin (frontend/backend na roznych portach) — endpoint (session-gated,
      // routes/clients.js) odpowiada 401, a `catch` ponizej po cichu czyscil podpowiedzi
      // bez zadnego komunikatu. Kazde INNE wywolanie axios w tym pliku juz to ma
      // (np. /api/equipment/available), to byla jedyna brakujaca kopia.
      const res = await axios.get(`${API_BASE}/api/clients/search?query=${encodeURIComponent(value)}`, { withCredentials: true });
      setClientSuggestions(res.data);
      setShowSuggestions(true);
    } catch {
      setClientSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleLastnameChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, lastname: value });
    fetchClientSuggestions(value);
  };

  const handleClientSuggestionClick = (client) => {
    const isReal = v => v !== null && typeof v !== 'undefined' && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'brak danych';

    setForm(prev => ({

      ...prev,
      firstname: isReal(client.firstname) ? client.firstname : prev.firstname,
      lastname:  isReal(client.lastname)  ? client.lastname  : prev.lastname,
     
     
      email:     isReal(client.email)     ? client.email     : prev.email,
      idNumber:  isReal(client.id_number) ? client.id_number  : prev.idNumber,
      phone:     isReal(client.phone_number) ? client.phone_number : prev.phone,
      // rozbite pola adresowe
      city: isReal(client.city) ? client.city : prev.city,
      postal_code: isReal(client.postal_code) ? client.postal_code : prev.postal_code,
      street: isReal(client.street) ? client.street : prev.street,
      building_number: isReal(client.building_number) ? client.building_number : prev.building_number,
      apartment_number: isReal(client.apartment_number) ? client.apartment_number : prev.apartment_number,
      // zachowaj bieżące wartości rabatu/note/paymentMethod
      discount: prev.discount,
      note: prev.note,
      paymentMethod: prev.paymentMethod,
    }));
    setShowSuggestions(false);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setValidationErrors({});
    setError('');
    // Bez tego np. „Oferta odłączona” zostawało obok błędu następnego zapisu.
    setSuccess('');

    const errors = {};
    if (!form.firstname.trim()) errors.firstname = t('validation.required', { field: t('reservation.firstname') });
    if (!form.lastname.trim()) errors.lastname = t('validation.required', { field: t('reservation.lastname') });
    // X.2: e-mail ALBO telefon (klient z ulicy), ta sama reguła co serwer.
    const kontakt = bledyKontaktu(form);
    if (kontakt.email) errors.email = t(kontakt.email);
    if (kontakt.phone) errors.phone = t(kontakt.phone);
    if (!dateFrom || !dateTo) errors.dateRange = t('validation.required', { field: t('reservation.range') });
    // if (!form.paymentMethod) errors.paymentMethod = t('validation.required', { field: t('reservation.paymentMethod') });
    if (selectedEquipment.length === 0) errors.equipment = t('validation.selectEquipment');

    // Limity kwot sprawdzamy tu i przy opuszczeniu pola, nigdy w trakcie pisania (U.25).
    // `discountCode`/`advanceCode` są liczone niżej z bieżących wartości; komunikaty pokazują pola.
    const amountsInvalid = !!(discountCode || advanceCode);
    if (amountsInvalid) showAmountChecks();

    if (Object.keys(errors).length > 0 || amountsInvalid) {
      setValidationErrors(errors);
                                                                                 setError(t('validation.fillAllFields'));
      scrollToTop();
      return;
    }

    if (offerConverted && sourceOfferId) {
      setError(t('reservation.offerGone.converted'));
      scrollToTop();
      return;
    }

    // Kwota rezerwacji tylko z wyceny, która wróciła (albo z ceny lokalnej po jej awarii).
    if (!quoteSettled) {
      setError(t('reservation.quotePending'));
      scrollToTop();
      return;
    }

    // X.2: link do zapłaty idzie mailem, więc bez e-maila w formularzu nie wychodzi. Pole wyboru znika
    // przy pustym e-mailu, ale zaznaczenie zostaje w stanie: bez tego warunku skasowanie e-maila
    // po zaznaczeniu blokowało zapis błędem niewidocznego pola terminu albo wysyłało link.
    const wyslijLink = form.sendPayLink && !!form.email.trim();

    // F.6: zaznaczony termin wpłaty poza zakresem. Link z terminem idzie po zapisie, więc bez tego
    // rezerwacja zapisałaby się, a link poszedłby bez terminu, który pracownik chciał ustawić.
    if (wyslijLink && linkiOnline && terminWplaty.wstrzymaj) {
      setError(t('paymentDeadline.invalid'));
      scrollToTop();
      return;
    }

    try {
      // Usuwamy całe wyszukiwanie klienta po numerze dowodu – backend tworzy/aktualizuje klienta po emailu.
      // 2) Przygotuj payload BEZ idNumber
      const {
        firstname, lastname, email, phone, discount, discountUnit, note,
        city, postal_code, street, building_number, apartment_number,
        advanceAmount, advancePaid
      } = form;

      let payload = {
        firstname, lastname, email, phone, discountUnit, note,
        discount: amountOrZero(discount),
        dateFrom: dateFrom ? formatLocalDate(dateFrom) : null,
        dateTo: dateTo ? formatLocalDate(dateTo) : null,
        equipment: selectedEquipment,
        totalPrice: totalPrice,     // cena przed rabatem (brutto)
        city, postal_code, street, building_number, apartment_number,
        address: `${street || ''} ${building_number || ''}${apartment_number?'/'+apartment_number:''}, ${postal_code || ''} ${city || ''}`.trim(),
        advance_amount: amountOrZero(advanceAmount),
        advance_paid: !!advancePaid,                   // NOWE
        deposit_override:
          // amountOrZero, nie Number: pole ma inputMode="decimal", więc „150,50” z polskiej
          // klawiatury dawało NaN i zapisywało kaucję 0 zł (ta sama przyczyna co K-1 w umowie).
          form.depositOverrideRaw !== ''
            ? amountOrZero(form.depositOverrideRaw)
            : null,
      };

      if (sourceOfferId) {
        payload.offerId = sourceOfferId;
      }

      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await axios.post(`${API_BASE}/api/reservation`, payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrf }
      });

      if (res.status === 200 || res.status === 201) {
        // WYZNACZ ID I DATĘ DLA NAWIGACJI
        const newReservationId =
          res.data?.reservationId ||
          res.data?.id ||
          res.data?.reservation_id;

        // Format lokalnej daty rozpoczęcia (fallback gdy backend ją zwróci)
        const startDateForNav = formatLocalDate(dateFrom) ||
          res.data?.start_date ||
          res.data?.dateFrom;

        // Emituj zdarzenia (jak wcześniej)
        try {
          if (res.data?.client) {
            window.dispatchEvent(new CustomEvent('clients:updated', { detail: res.data.client }));
          } else {
            window.dispatchEvent(new Event('clients:updated'));
          }
          window.dispatchEvent(new Event('reservations:updated'));
        } catch (e) {}

        // X.2: ostrzeżenia serwera (rezerwacja przypisana do klienta, do którego należy numer,
        // numer zajęty przez innego klienta). Do zamknięcia, bo po zapisie panel przechodzi do
        // kalendarza; dawniej odpowiedź je niosła, ale panel ich nie pokazywał.
        (Array.isArray(res.data?.warnings) ? res.data.warnings : [])
          .forEach((ostrzezenie) => toast.warning(String(ostrzezenie), { duration: 0 }));

        // P3.3-B: automatyczna wysyłka linku do zapłaty zaliczki (best-effort, nie blokuje zapisu).
        if (wyslijLink && maPremium() && !advancePaid && amountOrZero(advanceAmount) > 0 && newReservationId) {
          try {
            await axios.post(`${API_BASE}/api/payments/send-link`,
              {
                reservation_id: newReservationId, send: true,
                // F.6: termin wpłaty, gdy pracownik go zaznaczył (walidacja przed zapisem rezerwacji).
                ...(terminWplaty.terminIso ? { payment_deadline_at: terminWplaty.terminIso } : {}),
              },
              { withCredentials: true, headers: { 'X-CSRF-Token': csrf } });
          } catch (payLinkErr) {
            // F.2: odmowa planu (plan zmieniony w trakcie sesji) ma własny komunikat z „Chcę Premium”.
            if (!pokazBladPremium(payLinkErr)) {
              // Powód z serwera (np. „Płatności online są wstrzymane…”), bo samo „nie udało się”
              // nie mówiło, co zrobić (test odbiorczy płatności 12.10.2026).
              const powod = payLinkErr && payLinkErr.response && payLinkErr.response.data && payLinkErr.response.data.error;
              toast.error(powod
                ? t('reservation.save.paymentLinkFailedReason', { reason: String(powod) })
                : t('reservation.save.paymentLinkFailed'));
            }
          }
        }

        // P5.2: wgraj dokumentację wydania zebraną w formularzu (best-effort, nie blokuje
        // przejścia — resztę i tak można dodać z Kalendarza). Faza zawsze 'pickup' (wydanie).
        if (pendingDocs.length && newReservationId) {
          const nieudaneDocs = await wyslijPoKolei(pendingDocs, async (d) => {
            const fd = new FormData();
            fd.append('phase', 'pickup');
            if (d.note) fd.append('note', d.note);
            if (d.equipmentId) fd.append('equipment_id', d.equipmentId);
            if (d.file) {
              // Pomniejszone przed wysyłką (dysk produkcji), opis w utils/imageThumb.js.
              const zdjecie = await prepareUploadPhoto(d.file);
              fd.append('file', zdjecie);
              const thumb = await makeThumbnail(zdjecie); // P5.5
              if (thumb) fd.append('thumb', thumb, 'thumb.jpg');
            }
            await axios.post(`${API_BASE}/api/reservation-photos/${newReservationId}`, fd,
              { withCredentials: true, headers: { 'X-CSRF-Token': csrf } });
          });
          // Zwolnij podglądy blob: — nawigacja to routing kliencki (dokument żyje dalej),
          // więc bez tego URL-e wisiałyby do pełnego przeładowania strony.
          pendingDocs.forEach((d) => { if (d.previewUrl) URL.revokeObjectURL(d.previewUrl); });
          if (nieudaneDocs.length) {
            // Do zamknięcia (duration 0): po zapisie panel przechodzi do kalendarza i komunikat
            // z przyczyną łatwo przegapić, a mówi, co zrobić z wpisami w Kalendarzu.
            toast.error(komunikatNowejRezerwacji(nieudaneDocs, pendingDocs.length, t), { duration: 0 });
          }
        }

        // NAWIGACJA: przenieś użytkownika do szczegółów w kalendarzu
        if (newReservationId && startDateForNav) {
          navigate(
            `/calendar?date=${encodeURIComponent(startDateForNav)}&reservationId=${encodeURIComponent(newReservationId)}`
          );
        }

        // (opcjonalnie) ustaw komunikat sukcesu – użytkownik i tak przejdzie do kalendarza
        setSuccess(t('reservation.success.saved'));

       
        // Nie czyścimy od razu formularza przed nawigacją (nawigacja unmountuje komponent)
      } else {
        throw new Error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      const kodBledu = err.response?.data?.code;
      if (kodBledu === 'offer_not_found' && sourceOfferId) {
        // Oferty z wypełnienia już nie ma (wygasła albo ją usunięto). Bez odpięcia każda kolejna
        // próba wysyłała ten sam numer i dostawała ten sam błąd, więc rezerwacji nie dało się zapisać.
        setSourceOfferId(null);
        setOfferConverted(false);
        setSuccess('');
        setError(t('reservation.offerGone.notFound'));
      } else if (kodBledu === 'offer_already_converted' && sourceOfferId) {
        // Rezerwacja z tej oferty już jest: nie odpinamy jej po cichu, bo zapis bez powiązania
        // dałby tę samą rezerwację drugi raz. Wybór należy do użytkownika (akcje pod komunikatem).
        setOfferConverted(true);
        setSuccess('');
        setError(t('reservation.offerGone.converted'));
      } else if (err.response?.status === 409) {
        setError(err.response.data?.message || 'Wybrany sprzęt jest już zarezerwowany w tym terminie.');
      } else {
        const msgRaw = err.response?.data?.message || err.message || '';
        setError(msgRaw || t('common.error'));
        const msg = msgRaw.toLowerCase();
        const valErrors = {};
        if (/rabat|discount/.test(msg)) valErrors.discount = valErrors.discount || 'Sprawdź wartość rabatu.';
        if (/zaliczka|advance/.test(msg)) valErrors.advanceAmount = valErrors.advanceAmount || amountErrorMessage('amountOver', toPay, amountI18n);
        if (/kaucja|deposit/.test(msg)) valErrors.depositOverrideRaw = valErrors.depositOverrideRaw || 'Sprawdź kaucję.';
        if (Object.keys(valErrors).length) {
          setValidationErrors(prev => ({ ...prev, ...valErrors }));
        }
      }
      scrollToTop();
    }
  };

  // ZAMIANA: wcześniej Math.ceil((dateTo - dateFrom)/day) -> teraz zakres inkluzywny
  const days = daysInclusive(dateFrom, dateTo);
  const selectedEq = equipmentList.filter(eq => selectedEquipment.includes(eq.id));
  const isLongTermRental = days > 7;
  const currentQuoteKey = quoteKey(selectedEquipment, formatYMD(dateFrom), formatYMD(dateTo));
  // P7.5: whole fresh quote (items/bundles/appliedRules), not just grandTotal
  const priceQuoteData = (priceQuote && priceQuote.key === currentQuoteKey) ? priceQuote.data : null;
  // N.6: ten sam sprzęt i termin co oferta z wypełnienia, więc kwota i rabat z oferty, nie z bieżącej
  // wyceny (tak samo rozstrzyga serwer, POST /api/reservation z offerId). Inny sprzęt albo termin: cena
  // liczy się zwykle, a pole rabatu wraca do edycji z wartością z oferty.
  const cenaZOferty = (cenaOferty && sourceOfferId && !offerConverted
    && formatYMD(dateFrom) === cenaOferty.od && formatYMD(dateTo) === cenaOferty.dd
    && selectedEquipment.length === cenaOferty.sprzet.length
    && cenaOferty.sprzet.every((idSprzetu) => selectedEquipment.map(Number).includes(idSprzetu)))
    ? cenaOferty
    : null;
  // N.3: dni płatne z wyceny serwera, a do jej nadejścia i po jej błędzie lokalny zapas.
  const dniPlatneWyceny = dniPlatneZWyceny(priceQuoteData, dniPlatne(days, tabelaDniPlatnych));
  const chargeableDays = cenaZOferty && cenaZOferty.dni > 0 ? cenaZOferty.dni : dniPlatneWyceny;
  const quoteSettled = !!cenaZOferty || selectedEquipment.length === 0 || !dateFrom || !dateTo || quoteSettledKey === currentQuoteKey;
  const quotedTotal = priceQuoteData ? priceQuoteData.grandTotal : null;
  // Cena lokalna zaokrąglona do grosza: suma stawek bywa ułamkiem tuż poniżej kwoty z ekranu
  // (2 × 19,99 × 5 = 199,89999999999998), a ta liczba idzie do zapisu jako `totalPrice` i serwer
  // porównuje z nią rabat w zł.
  const totalPrice = cenaZOferty
    ? cenaZOferty.kwota
    : quotedTotal != null
      ? quotedTotal
      : roundToCents(selectedEq.reduce((sum, eq) => sum + getEquipmentDailyRate(eq, isLongTermRental), 0) * dniPlatneWyceny);
  // P7.5: price-visibility view-model; null (no/stale quote) → UI identical to pre-P7.5. Bieżąca
  // wycena nie opisuje ceny z oferty, więc przy niej rozbicia nie ma.
  const priceBreakdown = buildPriceBreakdown(cenaZOferty ? null : priceQuoteData, selectedEq, eq => getEquipmentDailyRate(eq, isLongTermRental));
  const equipmentGroups = groupEquipmentForDisplay(selectedEq, priceBreakdown);
  const itemView = (eq) => (priceBreakdown ? priceBreakdown.itemsById[eq.id] : null);
  const totalBail = selectedEq.reduce((sum, eq) => sum + Number(eq.bail || 0), 0); // ZMIANA: bez * chargeableDays
  // Rabat w zł wg jednostki, zawsze w granicach 0…wartość sprzętu (utils/amountField.js).
  const discountNumber = amountOrZero(form.discount);
  const advanceNumber = amountOrZero(form.advanceAmount);
  const linkiOnline = maPremium();
  const discountValue = cenaZOferty
    ? Math.min(cenaZOferty.rabatZl, totalPrice)
    : discountInPln(form.discount, form.discountUnit, totalPrice);
  const toPay = Math.max(0, totalPrice - discountValue);

  // NOWE: kwota po odjęciu opłaconej zaliczki
  const effectiveToPay = Math.max(
    0,
    toPay - (form.advancePaid ? advanceNumber : 0)
  );

  // Kody błędów pól kwot liczone z BIEŻĄCYCH wartości i limitów (komunikat nie jest stanem, patrz
  // utils/useAmountChecks.js): rabat w zł względem wartości sprzętu, zaliczka względem kwoty po
  // rabacie. Dopóki wartość sprzętu jest nieznana (0: brak sprzętu albo dat), zaliczki nie ma z czym
  // porównać — da się ją wpisać wcześniej, a zapis bez sprzętu nie dokłada „Maksymalnie 0,00 zł”
  // do komunikatu o brakującym sprzęcie. Przy znanej wartości limit 0 (rabat 100%) jest prawdziwy.
  const amountI18n = { t, locale: currentLanguage };
  // Rabat z oferty nie pochodzi z pola (pole jest wtedy wyłączone), więc nie ma czego sprawdzać.
  const discountCode = cenaZOferty ? null : discountError(form.discount, form.discountUnit, totalPrice);
  const advanceCode = advanceError(form.advanceAmount, toPay, totalPrice > 0);
  const discountNote = (amountChecksShown.discount && amountErrorMessage(discountCode, totalPrice, amountI18n))
    || validationErrors.discount
    || null;
  const advanceNote = (amountChecksShown.advance && amountErrorMessage(advanceCode, toPay, amountI18n))
    || validationErrors.advanceAmount
    || null;

  // Pisanie w polu chowa jego limit (wróci po opuszczeniu pola) i komunikat po błędzie z serwera.
  const setAmountField = (name, raw) => {
    setForm(prev => ({ ...prev, [name]: raw }));
    hideAmountCheck(name === 'discount' ? 'discount' : 'advance');
    setValidationErrors(prev => {
      if (!prev[name]) return prev;
      const n = { ...prev };
      delete n[name];
      return n;
    });
  };

  // USUNIĘTE: startOfToday (już nie ograniczamy wyboru dat do przyszłości)

  // USUNIĘTE: zależność od form.paymentMethod przy generowaniu kontraktu / walidacji
  // Stan generowania: napis i blokada przycisku na czas żądania, ref blokuje drugie kliknięcie
  // od razu (test odbiorczy 11.10.2026: każde kliknięcie to osobny render PDF na serwerze).
  const [generatingContract, setGeneratingContract] = useState(false);
  const generatingContractRef = useRef(false);
  const generateContract = async () => {
    if (generatingContractRef.current) return;
    if (
      !form.firstname || !form.lastname || !maKontakt(form) ||
      !dateFrom || !dateTo ||
      selectedEquipment.length === 0
    ) {
      setError('Wypełnij wymagane pola przed wygenerowaniem umowy');
      scrollToTop();
      return;
    }
    // Umowa z kwotą z wyceny, nie z ceny lokalnej sprzed jej powrotu (jak zapis rezerwacji).
    if (!quoteSettled) {
      setError(t('reservation.quotePending'));
      scrollToTop();
      return;
    }
    generatingContractRef.current = true;
    setGeneratingContract(true);
    try {
      const data = buildContractDataFromReservation({
        form: { ...form, discount: discountNumber, advanceAmount: advanceNumber },
        dateFrom,
        dateTo,
        selectedEquipmentIds: selectedEquipment,
        equipmentList,
        paymentMethods: [], // brak metody płatności
        days,
        chargeableDays,
        toPay,
        totalBail,
        discountValue,
        advanceAmount: advanceNumber,
        advancePaid: form.advancePaid
      });
      await sendAndDownloadContract(data, { t });
      // Toast zamiast banera nad formularzem: przycisk stoi na dole długiej strony, a baner
      // u góry był ponad 1000 px poza ekranem, więc sukcesu nie było widać (sprawdzenie
      // 12.10.2026). Bez przewijania, bo obok jest „Zapisz”; tak samo robi kalendarz.
      toast.success(t('reservation.contractGenerated'));
    } catch (error) {
      setError(t('reservationForm.contract.generateError', { message: error?.message || String(error) }));
      scrollToTop();
    } finally {
      generatingContractRef.current = false;
      setGeneratingContract(false);
    }
  };

  // Czy formularz jest "zmieniony" — używane do podświetlenia i włączenia przycisku "Wyczyść"
  const isDirty = useMemo(() => {
    const formChanged = JSON.stringify(form) !== JSON.stringify(initialForm);
    const datesChosen = !!dateFrom || !!dateTo;
    const equipmentChosen = selectedEquipment && selectedEquipment.length > 0;
    return formChanged || datesChosen || equipmentChosen;
  }, [form, dateFrom, dateTo, selectedEquipment, initialForm]);

  // DODANE: funkcje do obsługi oczekiwanego zakresu dat (potwierdzenie przy wstecznych)
  const applyPendingRange = () => {
    if (!pendingRange) return;
    setDateFrom(pendingRange.start);
    setDateTo(pendingRange.end);
    if (validationErrors.dateRange) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next.dateRange;
        return next;
      });
    }
    setPendingRange(null);
    setShowPastConfirm(false);
  };

  const cancelPendingRange = () => {
    setPendingRange(null);
    setShowPastConfirm(false);
  };

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon><CalendarIcon /></TitleIcon>
          {t('reservation.title') || 'Nowa Rezerwacja'}
        </Title>
        <Subtitle>{t('reservation.subtitle')}</Subtitle>
      </Header>

      {/* F.11: konto w przerwie zapisuje tylko do odczytu; serwer i tak odrzuci zapis. */}
      <TylkoOdczytWPrzerwie>
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <Message error data-cy="reservation-error">
            <XIcon />
            {error}
          </Message>
        )}
        {offerConverted && sourceOfferId && (
          <OfferGoneActions data-cy="reservation-offer-gone-actions">
            {/* Na dzień startu z wypełnienia: tam stoi rezerwacja powstała z oferty. */}
            <SecondaryButton
              type="button"
              onClick={() => navigate(dateFrom
                ? `/calendar?date=${encodeURIComponent(formatLocalDate(dateFrom))}`
                : '/calendar')}
              data-cy="reservation-offer-gone-calendar"
            >
              <CalendarIcon />
              {t('reservation.offerGone.openCalendar')}
            </SecondaryButton>
            <SecondaryButton
              type="button"
              data-cy="reservation-offer-gone-unlink"
              onClick={() => {
                setSourceOfferId(null);
                setOfferConverted(false);
                setError('');
                setSuccess(t('reservation.offerGone.unlinked'));
              }}
            >
              <XIcon />
              {t('reservation.offerGone.unlink')}
            </SecondaryButton>
          </OfferGoneActions>
        )}
        {success && (
          <Message>
            <CheckIcon />
            {success}
          </Message>
        )}
        {availabilityWarning && (
         <Message error>
           <XIcon />
           {availabilityWarning}
         </Message>
       )}
        <BasicDataPanel data-tour="reservation-client">
          <PanelHeader>
            <PanelIcon $tone="steel">
              <UserIcon />
            </PanelIcon>
            <PanelTitle>{t('reservation.basicData') || 'Dane podstawowe'}</PanelTitle>
          </PanelHeader>
          
          <InputGroup>
            <InputWrapper>
              <Label>
                <UserIcon />
                {t('reservation.firstname')}*
              </Label>
              <Input name="firstname" value={form.firstname} onChange={handleChange} required placeholder="Jan" error={validationErrors.firstname} data-cy="reservation-firstname" />
              {validationErrors.firstname && <FieldError><XIcon /> {validationErrors.firstname}</FieldError>}
            </InputWrapper>
            <InputWrapper style={{ position: 'relative' }}>
              <Label>
                <UserIcon />
                {t('reservation.lastname')}*
              </Label>
              <Input
                name="lastname"
                value={form.lastname}
                onChange={handleLastnameChange}
                autoComplete="off"
                required
                id="nazwisko"
                placeholder="Kowalski"
                error={validationErrors.lastname}
                data-cy="reservation-lastname"
              />
              {validationErrors.lastname && <FieldError><XIcon /> {validationErrors.lastname}</FieldError>}
              {showSuggestions && clientSuggestions.length > 0 && (
                <SuggestionsList>
                  {clientSuggestions.map(client => (
                    <SuggestionItem
                      key={client.id}
                      onClick={() => handleClientSuggestionClick(client)}
                      data-cy="reservation-client-suggestion"
                    >
                      <strong>{client.firstname} {client.lastname}</strong>
                      <br />
                      <small style={{ color: 'var(--text-muted)' }}>{client.email}</small>
                    </SuggestionItem>
                  ))}
                </SuggestionsList>
              )}
            </InputWrapper>
          </InputGroup>
        </BasicDataPanel>

        <Panel>
          <PanelHeader>
            <PanelIcon $tone="steel">
              <PhoneIcon />
            </PanelIcon>
            <PanelTitle>{t('reservation.contact') || 'Dane kontaktowe'}</PanelTitle>
          </PanelHeader>
          {/* X.2: bez gwiazdek przy e-mailu i telefonie, bo wymagany jest jeden z nich. */}
          <small data-cy="reservation-contact-hint" style={{ display: 'block', margin: '-4px 0 12px', color: 'var(--text-muted)', fontSize: '.85rem' }}>
            {t('reservation.contactHint')}
          </small>

          <Grid>
            <InputWrapper>
              <Label>
                <MailIcon />
                {t('reservation.email')}
              </Label>
              <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jan.kowalski@example.com" error={validationErrors.email} data-cy="reservation-email" />
              {validationErrors.email && <FieldError><XIcon /> {validationErrors.email}</FieldError>}
            </InputWrapper>

            <InputWrapper style={{ position: 'relative' }}>
              <Label>
                <MapPinIcon />
                {t('reservation.address') || 'Adres'}
              </Label>
              <Input
                name="address"
              value={(function(f){
                const parts = [];
                if (f.street) parts.push(f.street);
                if (f.building_number) parts.push(f.apartment_number ? `${f.building_number}/${f.apartment_number}` : f.building_number);
                const pcCity = [f.postal_code, f.city].filter(Boolean).join(' ');
                if (pcCity) parts.push(pcCity);
                return parts.length ? parts.join(', ') : '';
              })(form)}
                onClick={() => setShowAddressModal(true)}
                readOnly
                placeholder="Kliknij, aby wprowadzić adres"
                aria-label="Adres"
              />
                                                     <small style={{ display: 'block', marginTop: 6, color: 'var(--text-muted)', fontSize: '.85rem' }}>
                Kliknij, aby edytować ulicę / nr / kod pocztowy / miasto
              </small>
            </InputWrapper>
            <InputWrapper style={{ position: 'relative' }}>
              <Label>
                <FileTextIcon />
                {t('reservation.idNumber')}
              </Label>
              <Input
                name="idNumber"
                value={form.idNumber}
                onChange={handleIdNumberChange}
                autoComplete="off"
                placeholder="Numer dowodu (np. ABC123456)"
                error={validationErrors.idNumber}
              />
              {validationErrors.idNumber && <FieldError><XIcon /> {validationErrors.idNumber}</FieldError>}
            </InputWrapper>
            <InputWrapper>
              <Label>
                <PhoneIcon />
                {t('reservation.phone')}
              </Label>
              <Input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+48 123 456 789" error={validationErrors.phone} data-error={!!validationErrors.phone} data-cy="reservation-phone" />
              {validationErrors.phone && <FieldError><XIcon /> {validationErrors.phone}</FieldError>}
            </InputWrapper>
          </Grid>
        </Panel>

        <Grid>
          <TermPanel>
            <PanelHeader>
              <PanelIcon $tone="dusk">
                <CalendarIcon />
              </PanelIcon>
              <PanelTitle>{t('reservation.term') || 'Termin rezerwacji'}</PanelTitle>
            </PanelHeader>


            <DatepickerZIndexFix>
              <InputGroup>
                <InputWrapper style={{flex: 1}}>
                  <RangeLabelRow>
                    <Label style={{marginBottom: 0}}>
                      <CalendarIcon />
                      {t('reservation.range') || 'Zakres dat'}*
                    </Label>
                    <ClearRangeButton
                      type="button"
                      onClick={() => { setDateFrom(null); setDateTo(null); }}
                      disabled={!dateFrom && !dateTo}
                      title={t('reservation.clearRangeTitle')}
                    >
                      {t('common.clear')}
                    </ClearRangeButton>
                  </RangeLabelRow>

                  <DatePicker
                    popperModifiers={DOSUN_DO_EKRANU}
                    selected={dateFrom}
                    onChange={(dates) => {
                      const [start, end] = dates || [null, null];

                      // Potwierdzenie dopiero przy domknięciu zakresu
                      if (start && end && [start, end].some(isPastDate)) {
                        setPendingRange({ start, end });
                        setShowPastConfirm(true);
                        return;
                      }

                      setDateFrom(start);
                      setDateTo(end);
                      if (validationErrors.dateRange) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.dateRange;
                          return newErrors;
                        });
                      }
                    }}
                    startDate={dateFrom}
                    endDate={dateTo}
                    selectsRange
                    locale="pl"
                    /* usunięto minDate i filterDate, by umożliwić wybór dat wstecz */
                    dateFormat="yyyy-MM-dd"
                    shouldCloseOnSelect={false}
                    customInput={
                      <CustomRangeInput
                        start={dateFrom}
                        end={dateTo}
                        placeholderStart={t('reservation.from')}
                        placeholderEnd={t('reservation.to')}
                        error={validationErrors.dateRange}
                      />
                    }
                    dayClassName={(date) => {
                      const classes = [];
                      const day = date.getDay();
                      if (day === 0 || day === 6) classes.push('weekend');
                      if (isPastDate(date)) classes.push('past-day');
                      return classes.join(' ');
                    }}
                  />
                  {validationErrors.dateRange && <FieldError style={{ bottom: '-18px' }}><XIcon /> {validationErrors.dateRange}</FieldError>}
                </InputWrapper>
              </InputGroup>
            </DatepickerZIndexFix>
          </TermPanel>

          <PaymentPanel>
            <PanelHeader>
              <PanelIcon $tone="gold">
                <TagIcon />
              </PanelIcon>
              <PanelTitle>{t('reservation.paymentAndDiscount') || 'Rabat i zaliczka'}</PanelTitle>
            </PanelHeader>
            
            <InputGroup>
              <InputWrapper style={{ minWidth: 0, marginTop: 12 }}>
                <Label htmlFor="reservation-discount">
                  <TagIcon />
                  {t('reservation.discountAmount')}
                </Label>
                <AmountBox>
                  <DiscountField
                    id="reservation-discount"
                    value={form.discount}
                    unit={form.discountUnit}
                    onChange={raw => setAmountField('discount', raw)}
                    onUnitChange={unit => {
                      // Zmiana jednostki nie przelicza ani nie przycina wpisanej liczby; limit
                      // pokazujemy od razu, bo 150 w zł może być poprawne, a 150% już nie.
                      setForm(prev => ({ ...prev, discountUnit: unit }));
                      showAmountCheck('discount');
                    }}
                    onBlur={() => showAmountCheck('discount')}
                    placeholder={form.discountUnit === 'PLN' ? t('amountField.discountPlaceholderPln') : t('amountField.discountPlaceholderPercent')}
                    invalid={!!discountNote}
                    describedBy="reservation-discount-note"
                    dataCy="reservation-discount"
                    disabled={!!cenaZOferty}
                  />
                  <AmountNote id="reservation-discount-note" data-error={!!discountNote} data-cy="reservation-discount-note">
                    {cenaZOferty
                      ? t('reservation.offerPrice', { amount: formatMoney(toPay, { locale: currentLanguage }) })
                      : (discountNote
                        || (discountNumber > 0 && totalPrice > 0
                          ? t('amountField.afterDiscount', { amount: formatMoney(toPay, { locale: currentLanguage }) })
                          : null))}
                  </AmountNote>
                </AmountBox>
              </InputWrapper>

              <InputWrapper style={{ minWidth: 0, marginTop: 12 }}>
                <Label htmlFor="reservation-advance">
                  <CreditCardIcon />
                  {t('reservation.advance')}
                </Label>
                {/* P3.15 follow-up: brak ręcznego checkboxa „Opłacona" — nowa rezerwacja jest
                    nieopłacona; zaliczkę pobierasz linkiem/kartą/gotówką/przelewem (jedno źródło prawdy).
                    U.25: zaliczkę da się wpisać przed wyborem sprzętu; limit sprawdzamy po opuszczeniu
                    pola i przy zapisie, nie w trakcie pisania. */}
                <AmountBox>
                  <AmountField
                    id="reservation-advance"
                    name="advanceAmount"
                    value={form.advanceAmount}
                    onChange={raw => setAmountField('advanceAmount', raw)}
                    onBlur={() => showAmountCheck('advance')}
                    placeholder={t('amountField.advancePlaceholder')}
                    invalid={!!advanceNote}
                    describedBy="reservation-advance-note"
                    dataCy="reservation-advance"
                  />
                  <AmountNote id="reservation-advance-note" data-error={!!advanceNote} data-cy="reservation-advance-note">
                    {advanceNote}
                  </AmountNote>
                </AmountBox>
                {!form.advancePaid && advanceNumber > 0 && form.email.trim() && (
                  <>
                    {/* F.5: link do zapłaty online jest w Premium (serwer i tak odpowiada 403). */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '.8rem',
                      fontWeight: 600,
                      cursor: linkiOnline ? 'pointer' : 'default',
                      opacity: linkiOnline ? 1 : 0.55,
                      userSelect: 'none',
                      marginTop: 8
                    }}>
                      <input
                        type="checkbox"
                        name="sendPayLink"
                        checked={linkiOnline && form.sendPayLink}
                        disabled={!linkiOnline}
                        onChange={handleChange}
                        data-cy="reservation-send-pay-link"
                        style={{ width:16, height:16, accentColor:'var(--primary)' }}
                      />
                      Wyślij klientowi link do zapłaty (email)
                    </label>
                    {!linkiOnline && (
                      <PremiumNotice feature="payment_links" compact style={{ marginTop: 6 }} data-cy="reservation-pay-link-premium">
                        {t('reservation.payLinkPremium')}
                      </PremiumNotice>
                    )}
                    {/* F.6: „Anuluj, jeśli klient nie wpłaci do” przy linku do zaliczki (Premium). */}
                    {(form.sendPayLink || !linkiOnline) && (
                      <div style={{ marginTop: 8 }}>
                        <PaymentDeadlineField termin={terminWplaty} premium={linkiOnline} id="reservation-payment-deadline" />
                      </div>
                    )}
                  </>
                )}
              </InputWrapper>
            </InputGroup>
          </PaymentPanel>
        </Grid>

        <Panel>
          <PanelHeader data-tour="reservation-equipment">
            <PanelIcon $tone="pine">
              <PackageIcon />
            </PanelIcon>
            <PanelTitle>{t('reservation.selectedEquipment')}*</PanelTitle>
            <div style={{ marginLeft: 'auto' }} title={!dateFrom || !dateTo ? t('reservation.scan.pickDatesFirst') : undefined}>
              <ScanSources onScan={handleScanAdd} disabled={!dateFrom || !dateTo} compact dataCyPrefix="reservation-scan" />
            </div>
            <EquipmentButton
              type="button"
              enabled={!!dateFrom && !!dateTo}
              onClick={openEquipmentModal}
              disabled={!dateFrom || !dateTo}
              style={{ marginLeft: 8 }}
              data-cy="reservation-open-equipment"
            >
              <PlusIcon />
              {t('reservation.chooseEquipment')}
            </EquipmentButton>
          </PanelHeader>
          
        {selectedEquipment.length > 0 ? (
          <EquipmentList data-cy="reservation-equipment-list">
            {equipmentGroups.map((group, gi) => (
              <BundleFrame key={group.bundle ? `bundle-${gi}` : `eq-${group.items[0].id}`} bundle={group.bundle}>
                {group.items.map(eq => (
                  <EquipmentItem key={eq.id}>
                    <EquipmentInfo>
                      <PackageIcon />
                      <strong>{eq.name}</strong>
                      <span>({eq.inventory_number}, {eq.size}, {eq.brand}, {eq.category}, {eq.class})</span>
                    </EquipmentInfo>
                    {/* NOWE: cena za dzień, spójna ze szczegółami w kalendarzu */}
                    <EquipmentPriceBox>
                      <EquipmentPrice>
                        <ItemRate item={itemView(eq)} baseText={`${formatMoney(getEquipmentDailyRate(eq, isLongTermRental), { locale: currentLanguage })}/${t('reservation.perDay')}`} />
                      </EquipmentPrice>
                      <RuleBadges item={itemView(eq)} />
                    </EquipmentPriceBox>
                    <RemoveButton
                      type="button"
                      data-cy="reservation-equipment-remove"
                      onClick={() => removeEquipment(eq.id)}
                      title="Usuń sprzęt"
                    >
                      <TrashIcon />
                    </RemoveButton>
                  </EquipmentItem>
                ))}
              </BundleFrame>
            ))}
          </EquipmentList>
        ) : (
          <EmptyState error={validationErrors.equipment}>
            <div className="icon">
              <PackageIcon />
            </div>
            <div className="text">{validationErrors.equipment || t('reservation.noEquipment')}</div>
          </EmptyState>
        )}
        </Panel>

        {/* P5.2: dokumentacja stanu sprzętu przy WYDANIU (zdjęcia + notatki) — buforowana,
            wgrywana po utworzeniu rezerwacji. Zwrot dokumentuje się później z Kalendarza. */}
        <Panel>
          <PanelHeader>
            <PanelIcon $tone="clay">
              <DocumentIcon />
            </PanelIcon>
            <PanelTitle>Dokumentacja stanu sprzętu (wydanie)</PanelTitle>
          </PanelHeader>

          {pendingDocs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {pendingDocs.map((d) => (
                <DocEntry key={d.id}>
                  {d.previewUrl && (
                    <img src={d.previewUrl} alt="Podgląd" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {d.note && <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{d.note}</div>}
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {d.equipmentId
                        ? (selectedEq.find(e => String(e.id) === String(d.equipmentId))?.name || 'Sprzęt')
                        : 'Cały zestaw'}
                      {d.fileName ? ` · ${d.fileName}` : ''}
                    </div>
                  </div>
                  <DocRemove type="button" onClick={() => removePendingDoc(d.id)}>{t('common.delete')}</DocRemove>
                </DocEntry>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <SimpleSelect
              value={docEquipmentId}
              onChange={setDocEquipmentId}
              style={{ minWidth: 180 }}
              options={[
                { value: '', label: 'Cały zestaw' },
                ...selectedEq.map((eq) => ({ value: String(eq.id), label: `${eq.name}${eq.inventory_number ? ` (${eq.inventory_number})` : ''}` })),
              ]}
            />
            <DocFileButton>
              <CameraIcon />
              {docFiles.length ? `Wybrano: ${docFiles.length}` : 'Wybierz zdjęcia'}
              <input
                ref={docFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                // Bez `capture`: na iPhonie ten atrybut otwierał od razu aparat i nie dało się
                // wybrać zdjęcia z galerii (sprawdzone na telefonie 27.09.2026). Bez niego iOS
                // pokazuje wybór „Zrób zdjęcie / Biblioteka zdjęć”.
                multiple
                onChange={(e) => setDocFiles(e.target.files ? Array.from(e.target.files) : [])}
              />
            </DocFileButton>
          </div>
          {/* FAZA T, T.4: dane osobowe na zdjęciu dowodowym to twarze, tablice i dokumenty. */}
          <div data-cy="photo-crop-hint" style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4, marginTop: 8 }}>
            {t('photos.cropHint')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
            <Input
              type="text"
              placeholder="Notatka (opcjonalnie)"
              value={docNote}
              onChange={(e) => setDocNote(e.target.value)}
              maxLength={2000}
              style={{ flex: 1, minWidth: 220 }}
            />
            <SecondaryButton type="button" onClick={addPendingDoc}>
              + Dodaj do dokumentacji
            </SecondaryButton>
          </div>
          {selectedEquipment.length === 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 10 }}>
              Zdjęcia i notatki zapiszą się przy tworzeniu rezerwacji.
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHeader>
            <PanelIcon $tone="clay">
              <FileTextIcon />
            </PanelIcon>
            <PanelTitle>{t('reservation.notes') || 'Notatki'}</PanelTitle>
          </PanelHeader>
          
          <InputWrapper>
            <Input
              as="textarea"
              name="note"
              value={form.note}
              onChange={handleChange}
              placeholder="Dodatkowe uwagi..."
              rows={3}
              style={{ resize: 'vertical', minHeight: '80px' }}
            />
          </InputWrapper>
        </Panel>

        <Panel>
          <PanelHeader>
            <PanelIcon $tone="gold">
              <CreditCardIcon />
            </PanelIcon>
            <PanelTitle>{t('reservation.summary') || 'Podsumowanie'}</PanelTitle>
          </PanelHeader>
          
          <SummaryTable>
            <SummaryRow>
              <SummaryLabel>{t('reservation.days')}</SummaryLabel>
              <SummaryValue data-cy="reservation-days">{days} {t('pricing.paidDays', { days: pokazDniPlatne(chargeableDays) })}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('reservation.equipmentPrice')}</SummaryLabel>
              <SummaryValue data-cy="reservation-total-price">{formatMoney(totalPrice, { locale: currentLanguage })}<QuotePendingNote pending={quotePending && !cenaZOferty} /></SummaryValue>
            </SummaryRow>
            <SummaryBreakdownRows breakdown={priceBreakdown} Row={SummaryRow} Label={SummaryLabel} Value={SummaryValue} />
            <SummaryRow>
              <SummaryLabel>
                {t('reservation.discount')} {form.discountUnit === '%' ? `(${discountNumber.toLocaleString(currentLanguage)}%)` : `(${formatMoney(discountNumber, { locale: currentLanguage })})`}
              </SummaryLabel>
              <SummaryValue>-{formatMoney(discountValue, { locale: currentLanguage })}</SummaryValue>
            </SummaryRow>
            <SummaryRow style={{ color: 'var(--info)' }}>
              <SummaryLabel style={{ color: 'var(--info)' }}>{t('reservation.bail')}</SummaryLabel>
              <SummaryValue style={{ color: 'var(--info)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DepositInput
                    type="text"
                    inputMode="decimal"
                    value={form.depositOverrideRaw}
                    data-cy="reservation-deposit"
                    placeholder={totalBail.toFixed(2)}
                    onChange={e => setForm(prev => ({ ...prev, depositOverrideRaw: e.target.value }))}
                    style={{ width: 120 }}
                    data-error={!!validationErrors.depositOverrideRaw}
                  />
                  <span>PLN</span>
                  {form.depositOverrideRaw !== '' && (
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, depositOverrideRaw: '' }))}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        padding: 4
                      }}
                      title={t('reservation.clearOverride')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('reservation.advance')} {form.advancePaid ? t('reservation.advancePaid') : t('reservation.advanceUnpaid')}</SummaryLabel>
              <SummaryValue>{formatMoney(advanceNumber, { locale: currentLanguage })} {form.advancePaid && '(-)'}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t('reservation.remainingToPay')}</SummaryLabel>
              <SummaryValue>{formatMoney(effectiveToPay, { locale: currentLanguage })}</SummaryValue>
            </SummaryRow>
          </SummaryTable>
        </Panel>

        <ButtonGroup>
          <SecondaryButton 
            type="button" 
            active={isDirty}
            disabled={!isDirty}
            data-cy="reservation-clear"
            onClick={() => {
              if (!isDirty) return;
              resetReservation();
              setError('');
              setSuccess(t('reservation.cleared'));
              setTimeout(() => setSuccess(''), 5000);
              scrollToTop();
            }}
          >
            <XIcon />
            {t('common.clear')}
          </SecondaryButton>
          
          <SecondaryButton 
            type="button" 
            onClick={generateContract}
            disabled={
              generatingContract ||
              !form.firstname || !form.lastname || !maKontakt(form) ||
              !dateFrom || !dateTo ||
              selectedEquipment.length === 0 || !quoteSettled
            }
            aria-busy={generatingContract ? 'true' : undefined}
            data-cy="reservation-new-generate-contract"
          >
            <DocumentIcon />
            {generatingContract ? t('reservation.generatingContract') : t('reservation.generateContract')}
          </SecondaryButton>
          
          <PrimaryButton
            type="submit"
            disabled={selectedEquipment.length === 0 || !quoteSettled} /* bez sprzętu i w trakcie wyceny nie zapisujemy */
            title={!quoteSettled ? t('reservation.quotePending') : undefined}
            data-cy="reservation-save"
          >
            <CheckIcon />
            {t('common.save')}
          </PrimaryButton>
        </ButtonGroup>
      </form>
      </TylkoOdczytWPrzerwie>

      <InventoryModal
        isOpen={showEquipmentModal}
        onClose={() => setShowEquipmentModal(false)}
        equipmentList={equipmentList}
        selectedIds={selectedEquipment}
        onToggle={(id) => {
          setSelectedEquipment(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
          );
        }}
      />
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        values={form}
        onSave={(vals) => {
          setForm(prev => ({ ...prev, ...vals }));
        }}
      />
      {showPastConfirm && (
        <ModalOverlay onClick={cancelPendingRange}>
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420, textAlign: 'center' }}
          >
            <ModalHeader style={{ borderBottom: 'none', marginBottom: 12, paddingBottom: 0 }}>
              <ModalTitle style={{ gap: 8 }}>
                <CalendarIcon />
                Potwierdź wybór dat
              </ModalTitle>
              <CloseButton onClick={cancelPendingRange}>
                <XIcon />
              </CloseButton>
            </ModalHeader>

            <p style={{ color: 'var(--text-muted)', margin: '12px 0 24px' }}>
              Wybrano zakres, który zawiera daty w przeszłości.
              Czy na pewno chcesz go ustawić?
            </p>

            <ButtonGroup style={{ justifyContent: 'center', marginTop: 0 }}>
              <SecondaryButton type="button" onClick={cancelPendingRange}>
                <XIcon /> {t('common.cancel')}
              </SecondaryButton>
              <PrimaryButton type="button" onClick={applyPendingRange}>
                <CheckIcon /> Ustaw zakres
              </PrimaryButton>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
      <DatepickerTheme />
    </Container>
  );
}

//
// DODANE: komponent niestandardowego wejścia dla zakresu (dwa pola "Od" / "Do")
//
const CustomRangeInput = React.forwardRef(({ start, end, onClick, placeholderStart, placeholderEnd, className, error }, ref) => {
  const pad = n => String(n).padStart(2, '0');
  const formatDate = (d) => {
    if (!d) return '';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  return (
    <RangeInputContainer className={className} ref={ref} onClick={onClick} error={error} data-cy="reservation-date-range">
      <input type="text" readOnly value={formatDate(start)} placeholder={placeholderStart} aria-label={placeholderStart} data-cy="reservation-date-from"/>
      <span className="range-sep">→</span>
      <input type="text" readOnly value={formatDate(end)} placeholder={placeholderEnd} aria-label={placeholderEnd} data-cy="reservation-date-to"/>
    </RangeInputContainer>
  );
});

const RangeLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;
// --- poprawiony ClearRangeButton (zamień cały blok) ---
const ClearRangeButton = styled.button`
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-muted);
  font-size: 0.65rem;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: .5px;
  cursor: pointer;
  transition: all .2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover:not(:disabled) {
    color: var(--danger);
    border-color: var(--danger);
    background: rgba(239,68,68,0.08);
  }
  &:disabled {
    opacity: .4;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  border: 1px solid ${p => p.error ? 'rgba(239,68,68,0.35)' : 'var(--input-border)'};
  background: var(--panel-bg);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  min-height: 120px;
  justify-content: center;

  .icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(59,130,246,0.12);
    color: var(--primary);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-primary);
  }

  .text {
    font-weight: 600;
    color: ${p => p.error ? 'var(--danger)' : 'var(--text-muted)'};
    line-height: 1.3;
  }
`;

function AddressModal({ isOpen, onClose, values, onSave }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState({
    street: '',
    building_number: '',
    apartment_number: '',
    postal_code: '',
    city: ''
  });

  useEffect(() => {
    if (isOpen) {
      setLocal({
        street: values.street || '',
        building_number: values.building_number || '',
        apartment_number: values.apartment_number || '',
        postal_code: values.postal_code || '',
        city: values.city || ''
      });
    }
  }, [isOpen, values]);

  const handleChange = e => {
    const { name, value } = e.target;
    setLocal(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave(local);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
        <ModalHeader>
          <ModalTitle>
            <MapPinIcon />
            {t('reservation.addressEdit') || 'Edycja adresu'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
        </ModalHeader>

        <Grid style={{ marginBottom: 8 }}>
          <InputWrapper>
            <Label><MapPinIcon /> Ulica</Label>
            <Input
              name="street"
              value={local.street}
              onChange={handleChange}
              placeholder="np. Lipowa"
            />
          </InputWrapper>
          <InputWrapper>
            <Label><MapPinIcon /> Nr budynku</Label>
            <Input
              name="building_number"
              value={local.building_number}
              onChange={handleChange}
              placeholder="np. 12"
            />
          </InputWrapper>
          <InputWrapper>
            <Label><MapPinIcon /> Nr lokalu</Label>
            <Input
              name="apartment_number"
              value={local.apartment_number}
              onChange={handleChange}
              placeholder="np. 4A"
            />
          </InputWrapper>
          <InputWrapper>
            <Label><MapPinIcon /> Kod pocztowy</Label>
            <Input
              name="postal_code"
              value={local.postal_code}
              onChange={handleChange}
              placeholder="00-000"
            />
          </InputWrapper>
          <InputWrapper>
            <Label><MapPinIcon /> Miasto</Label>
            <Input
              name="city"
              value={local.city}
              onChange={handleChange}
              placeholder="np. Zakopane"
            />
          </InputWrapper>
        </Grid>

        <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: 0 }}>
          <SecondaryButton type="button" onClick={onClose}>
            <XIcon /> {t('common.cancel')}
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSave}>
            <CheckIcon /> {t('common.save')}
          </PrimaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}